// NOTE — stays on the PRIVATE surface (waf_advanced_strategy type 4) on purpose.
// A public Open-API route exists (GET/POST /:id/waf/cc-protections,
// PUT/DELETE .../:ruleId — BE: cobra/waf + waap_waf_public_ops.go), but it
// does NOT map cleanly to this feature, so it was left private:
//   1) No 1:1 FE file. CC Protection here rides on the SHARED, config-agnostic
//      advanced-strategy.ts hook that also backs 8 sibling tabs (anti-leech,
//      brute-force, xml, weak-password, vuln-scanning, honeypot, cookie,
//      illegal-download). The public route is CC-protection-only, so repointing
//      that shared module would break the siblings.
//   2) Different data model. Private uses a generic envelope
//      {type,name,description,sites[],config{...}} where config.is_each_path is a
//      STRING, config.paths is a newline STRING, config.dimension_key,
//      limit_times/limit_seconds, capitalized action ("Block"), config.auto_black_ip.
//      Public is a flat dedicated DTO: {id,rate_limit_times,rate_limit_seconds,
//      metric,cumulative_enabled:bool,paths:[]string,action(lowercase),
//      auto_web_acl_duration_seconds,websites:[]int64}. Not a field-rename swap.
//   3) The companion public PUT /:id/waf/policy (protection_policy preset
//      1-9 per website) has no existing FE counterpart at all.
// Revisit only if a dedicated public CC-Protection page is built separately.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_CC_PROTECTION,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';

import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CCProtectionForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_CC_PROTECTION;

/** Action policies. Only "Block" is confirmed from a captured payload; Redirect /
 *  By-pass values are best-effort and may need adjusting against the portal. */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Redirect', label: 'Redirect' },
  { value: 'By-pass', label: 'By-pass' },
] as const;

/** Metrics strategy (dimension_key). Only "IP" is confirmed. */
const METRICS = [{ value: 'IP', label: 'IP' }] as const;

/** The CC Protection (type 4) per-type config. `is_each_path` is a STRING
 *  ("false" = cumulative across all paths, "true" = per-path). */
interface CCConfig {
  is_each_path: string;
  dimension_key: string;
  paths: string;
  limit_seconds: number;
  limit_times: number;
  action: string;
  block_value: number;
  delay: number;
  auto_black_ip: number;
  redirect_value: string;
}

interface FormState {
  name: string;
  description: string;
  sites: number[];
  limit_times: number;
  limit_seconds: number;
  dimension_key: string;
  cumulative: boolean;
  paths: string;
  action: string;
  block_value: number;
  delay: number;
  auto_black_ip: number;
  redirect_value: string;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  limit_times: 100,
  limit_seconds: 1,
  dimension_key: 'IP',
  cumulative: true,
  paths: '',
  action: 'Block',
  block_value: 418,
  delay: 0,
  auto_black_ip: 0,
  redirect_value: '',
});

/**
 * Add / Edit a CC Protection rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 4). Builds the type-specific `config`; the BE adds the
 * common envelope keys. Routes: aiwaf/advanced/cc-protection/new + /:id.
 */
export function CCProtectionForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<CCConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as CCConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      limit_times: c.limit_times ?? 100,
      limit_seconds: c.limit_seconds ?? 1,
      dimension_key: c.dimension_key || 'IP',
      // is_each_path "false" → cumulative on (default true when absent).
      cumulative: c.is_each_path !== 'true',
      paths: c.paths ?? '',
      action: c.action || 'Block',
      block_value: c.block_value ?? 418,
      delay: c.delay ?? 0,
      auto_black_ip: c.auto_black_ip ?? 0,
      redirect_value: c.redirect_value ?? '',
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<CCConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'cc_protection_rule', 'create');
      show({ tone: 'success', title: 'CC Protection rule created' });
      navigate(`${base}/aiwaf/advanced?tab=cc-protection`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<CCConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'cc_protection_rule', 'edit');
      show({ tone: 'success', title: 'CC Protection rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=cc-protection`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const pathsValid = form.paths.trim().length > 0;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit = nameValid && pathsValid && form.limit_times > 0 && form.limit_seconds > 0 && actionValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<CCConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        is_each_path: form.cumulative ? 'false' : 'true',
        dimension_key: form.dimension_key,
        paths: form.paths,
        limit_seconds: form.limit_seconds,
        limit_times: form.limit_times,
        action: form.action,
        block_value: form.block_value,
        delay: form.delay,
        auto_black_ip: form.auto_black_ip,
        redirect_value: form.action === 'Redirect' ? form.redirect_value.trim() : '',
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit CC Protection Rule' : 'CC Protection Rule' },
            ]}
          />
        }
        title="CC Protection Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Rate Limit"
                required
                description="Threshold of the rate limit. Default: 100 requests per second."
              >
                {() => (
                  <div className={styles.rateRow}>
                    <Input
                      type="number"
                      min={1}
                      value={String(form.limit_times)}
                      className={styles.rateInput}
                      onChange={(e) => set('limit_times', Number(e.target.value))}
                    />
                    <span className={styles.muted}>time(s) in</span>
                    <Input
                      type="number"
                      min={1}
                      value={String(form.limit_seconds)}
                      className={styles.rateInput}
                      onChange={(e) => set('limit_seconds', Number(e.target.value))}
                    />
                    <span className={styles.muted}>second(s)</span>
                  </div>
                )}
              </Field>

              <Field
                label="Metrics"
                required
                description="Strategy used for calculating the metrics. IP means the calculation is based on the same IP address."
              >
                {() => (
                  <Select value={form.dimension_key} onValueChange={(v) => set('dimension_key', v)}>
                    <SelectTrigger className={styles.selectMd}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <CheckboxField
                checked={form.cumulative}
                onChange={(v) => set('cumulative', v)}
                label="Enable Cumulative Calculation"
                description="Cumulative calculation is based on the total request count across all paths. Otherwise, the rate limit is calculated individually per path."
              />

              <Field
                label="Paths"
                required
                description="Paths validated against the rate limit. Enter multiple paths (regex allowed) separated by line breaks."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={form.paths}
                    maxLength={2000}
                    placeholder="/"
                    onChange={(e) => set('paths', e.target.value)}
                  />
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Configure how requests that satisfy the triggering condition are handled.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className={styles.actionRow}>
                <Field label="Delay (second(s))" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={0}
                      value={String(form.delay)}
                      className={styles.numField}
                      onChange={(e) => set('delay', Number(e.target.value))}
                    />
                  )}
                </Field>
                {form.action === 'Block' && (
                  <Field label="Response Status Code" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={100}
                        max={599}
                        value={String(form.block_value)}
                        className={styles.numField}
                        onChange={(e) => set('block_value', Number(e.target.value))}
                      />
                    )}
                  </Field>
                )}
                {form.action === 'Redirect' && (
                  <Field label="Redirect Path" required>
                    {(props) => (
                      <Input
                        {...props}
                        value={form.redirect_value}
                        placeholder="/blocked"
                        className={styles.redirectInput}
                        onChange={(e) => set('redirect_value', e.target.value)}
                      />
                    )}
                  </Field>
                )}
              </div>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Auto-Web ACL</CardTitle>
              <CardDescription>If a client's IP meets the criteria it is added to the Web ACL for the configured duration; during that time its requests are handled by the action above.</CardDescription>
            </CardHeader>
            <CardBody>
            <Field label="Duration (second(s))" required>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={String(form.auto_black_ip)}
                  className={styles.durationInput}
                  onChange={(e) => set('auto_black_ip', Number(e.target.value))}
                />
              )}
            </Field>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=cc-protection`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}
